/**
 * Send-message hook with optimistic UI + reconcile/rollback.
 *
 *   const { send, isSending, error } = useSendMessage(conversationId)
 *   await send({ body: "hi", replyTo: someMessageId })
 *
 * Flow:
 *   1. Generate a client-side temp uuid; build a placeholder `WireMessage`
 *      with `senderId = me.id` and `createdAt = now()`.
 *   2. `addPendingSend(tempMsg)` → message renders immediately at the
 *      bottom of the thread; `pendingSends` records the temp id.
 *   3. POST `messengerApi.sendMessage`. The `clientId` field carries the
 *      same temp uuid — chat O15 will use it for true idempotent retry;
 *      v1 server accepts it as a passthrough.
 *   4. **Success** → `reconcilePendingSend(tempId, real)` swaps the temp
 *      row in-place. The channel `message:new` event (which races the
 *      REST response) is tolerated by `appendWireMessage`'s de-dupe.
 *   5. **Failure** → `rollbackPendingSend(tempId)` removes the temp row
 *      and surfaces the error to the caller via `error`.
 *
 * Returns a stable `send` callback so the message input can pass it
 * directly to its on-submit handler.
 */

import { useCallback, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type {
  Message as WireMessage,
  MessageType,
  SendMessageInput,
} from '../services/messenger.wire'
import { sendMessageToConversation } from '../services/messenger.socket'
import { sendBroadcast } from '@/sdk/instavcMessenger-instance'
import { useMessengerStore } from '../store/messenger.store'
import { messengerApi } from '../services/messenger.api'
import { surfaceBroadcastCopies } from '../services/broadcast-copies'

interface UseSendMessage {
  send: (input: SendMessageInput) => Promise<WireMessage | null>
  isSending: boolean
  error: string | null
}

interface SendOptions {
  /**
   * The target is a BROADCAST LIST, not a conversation: route through
   * `sdk.broadcasts.send()` so the server fans the message into every
   * recipient's DM (plus the owner's copy in this thread) instead of pushing it
   * to the topic, where only the owner would ever see it.
   */
  isBroadcast?: boolean
  /** Fired after a successful fan-out (the list row's preview/time changed). */
  onBroadcastSent?: () => void
}

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Fallback — not cryptographically strong but unique enough for a temp id.
  return 'temp-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function useSendMessage(
  conversationId: string | null | undefined,
  opts: SendOptions = {},
): UseSendMessage {
  const { isBroadcast = false, onBroadcastSent } = opts
  const me = useAuthStore((s) => s.user)
  // Chat identity for the optimistic row's `senderId` = contactPresenceId (so it
  // matches the server-stored senderId + the fan-out echo, and `isSentByMe`
  // resolves). The socket send still authenticates as the auth user (`me.id`).
  const presenceId = useAuthStore((s) => s.presenceId)
  const addPending     = useMessengerStore((s) => s.addPendingSend)
  const reconcile      = useMessengerStore((s) => s.reconcilePendingSend)
  const rollback       = useMessengerStore((s) => s.rollbackPendingSend)
  const markCopies     = useMessengerStore((s) => s.markBroadcastCopies)

  const [isSending, setIsSending] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)

  const send = useCallback(
    async (input: SendMessageInput): Promise<WireMessage | null> => {
      if (!conversationId) {
        setError('no conversation')
        return null
      }
      if (!me?.id || !presenceId) {
        setError('not authenticated')
        return null
      }
      const tempId = randomUuid()
      const type: MessageType = input.type ?? 'text'
      const now = new Date().toISOString()
      const temp: WireMessage = {
        id:             tempId,
        conversationId,
        senderId:       presenceId,
        type,
        body:           input.body ?? null,
        replyTo:        input.replyTo ?? null,
        callPayload:    null,
        editedAt:       null,
        deletedAt:      null,
        createdAt:      now,
        reactions:      [],
        attachments:    input.attachments ?? [],
      }

      setError(null)
      setIsSending(true)
      addPending(temp)

      // Signal-driven send: push to the conversation channel; the signaling
      // server fans out `message:new` and forwards to the storage pipeline.
      // `clientMsgId == tempId` so the optimistic row, the broadcast, and the
      // stored copy all share one id — the store dedupe/reconcile just works.
      try {
        // Broadcast list: ONE REST call; the server ensures a DM per recipient
        // and delivers into each, then echoes the owner's copy id back.
        if (isBroadcast) {
          const result = await sendBroadcast(me.id, conversationId, input.body ?? '')
          const full: WireMessage = { ...temp, id: result.messageId || tempId }
          reconcile(tempId, full)
          // My copy in each recipient's DM gets the broadcast mark there.
          const copies = result.copies ?? []
          markCopies(copies.map((c) => c.messageId))
          // ...and lands in my chats, including a DM this send just created,
          // which nothing else tells me about. Not awaited: the list thread is
          // done, and the fetches it may need are the other chats' business.
          void surfaceBroadcastCopies(
            copies,
            { senderId: presenceId, body: (input.body ?? '').trim(), type, sentAt: now },
            {
              hasConversation: (id) => useMessengerStore.getState().wireConversations.has(id),
              getConversation: (id) => messengerApi.getConversation(id),
              upsertConversation: (c) => useMessengerStore.getState().upsertWireConversation(c),
              appendMessage: (m) => useMessengerStore.getState().appendWireMessage(m),
            },
          )
          onBroadcastSent?.()
          return full
        }
        const { message } = await sendMessageToConversation(
          me.id,
          conversationId,
          { body: input.body, clientMsgId: tempId, messageType: type, replyTo: input.replyTo ?? null, attachments: input.attachments },
          me.name,
        )
        const full: WireMessage = {
          ...temp,
          id: message.id ?? tempId,
          body: message.body ?? temp.body,
          createdAt: (message as { createdAt?: string }).createdAt ?? now,
        }
        reconcile(tempId, full)
        return full
      } catch (err: unknown) {
        rollback(tempId)
        setError(err instanceof Error ? err.message : 'failed to send')
        return null
      } finally {
        setIsSending(false)
      }
    },
    [conversationId, me?.id, me?.name, presenceId, addPending, reconcile, rollback, markCopies, isBroadcast, onBroadcastSent],
  )

  return { send, isSending, error }
}
